import {
	AdjustmentKey,
	AdjustmentRange,
	Adjustments,
	ColorProfileName,
	Curve,
	MaskAdjustmentKey,
	MaskGroup,
	MaskGroupAdjustments,
	PointColorHSL,
} from "@/core/types"
import { clamp, lerp } from "./utils/math"
import { createMaskGroup } from "@/store/editor/mask-utils"
import { ImageInfoRecord, PhotoRecord } from "@/lib/catalog"
import { Photo } from "./photo"
import { isJpegFile } from "@/utils/file"

export const getDefaultAdjustments = (
	source: PhotoRecord | File | Photo | null = null,
): Adjustments => {
	let isJpeg: boolean
	if (!source) {
		isJpeg = false
	} else if (source instanceof File) {
		isJpeg = isJpegFile(source.name)
	} else if (source instanceof Photo) {
		isJpeg = source.type === "bitmap"
	} else {
		isJpeg = isJpegFile(source.fileName)
	}

	return {
		colorProfile: isJpeg ? ColorProfileName.Standard : null,

		enableLensCorrections: null,
		correctLensDistortion: true,
		lensDistortionCorrectionFactor: 1.0,
		correctLensVignette: false,
		lensVignetteCorrectionFactor: 1.0,
		correctLensChromaticAberration: true,

		temperature: undefined,
		tint: undefined,

		exposure: 0,
		shadows: 0,
		highlights: 0,
		whites: 0,
		blacks: 0,
		contrast: 0,
		saturation: 0,
		vibrance: 0,

		vignette: 0,
		vignetteFeather: 0.5,
		vignetteMidpoint: 1.0,
		vignetteRoundness: 0,
		vignetteHighlights: 0,

		texture: 0,
		clarity: 0,
		dehaze: 0,

		curvesAll: [
			[0, 0],
			[255, 255],
		],
		curvesRed: [
			[0, 0],
			[255, 255],
		],
		curvesGreen: [
			[0, 0],
			[255, 255],
		],
		curvesBlue: [
			[0, 0],
			[255, 255],
		],

		hueRed: 0,
		saturationRed: 0,
		luminanceRed: 0,
		hueOrange: 0,
		saturationOrange: 0,
		luminanceOrange: 0,
		hueYellow: 0,
		saturationYellow: 0,
		luminanceYellow: 0,
		hueGreen: 0,
		saturationGreen: 0,
		luminanceGreen: 0,
		hueAqua: 0,
		saturationAqua: 0,
		luminanceAqua: 0,
		hueBlue: 0,
		saturationBlue: 0,
		luminanceBlue: 0,
		huePurple: 0,
		saturationPurple: 0,
		luminancePurple: 0,
		hueMagenta: 0,
		saturationMagenta: 0,
		luminanceMagenta: 0,

		calibrationRedHue: 0,
		calibrationRedSat: 0,
		calibrationGreenHue: 0,
		calibrationGreenSat: 0,
		calibrationBlueHue: 0,
		calibrationBlueSat: 0,

		parametricDarksStop: 0.25,
		parametricMidsStop: 0.5,
		parametricLightsStop: 0.75,
		parametricHighlights: 0,
		parametricLights: 0,
		parametricDarks: 0,
		parametricShadows: 0,

		gradingShadowsHue: 0,
		gradingShadowsSaturation: 0,
		gradingShadowsLuminance: 0,
		gradingMidtonesHue: 0,
		gradingMidtonesSaturation: 0,
		gradingMidtonesLuminance: 0,
		gradingHighlightsHue: 0,
		gradingHighlightsSaturation: 0,
		gradingHighlightsLuminance: 0,
		gradingGlobalHue: 0,
		gradingGlobalSaturation: 0,
		gradingGlobalLuminance: 0,
		gradingBlending: 0.5,
		gradingBalance: 0,

		luminanceNoiseReduction: 0,
		colorNoiseReduction: isJpeg ? 0 : 0.75,
		sharpen: isJpeg ? 0 : 0.4,

		cropStraighten: 0,
		crop: [0, 0, 1, 1],
		cropRatio: [0, 0],

		orientation: 0,
		flipX: false,
		flipY: false,

		lensDistortion: 0,
		perspectiveVertical: 0,
		perspectiveHorizontal: 0,
		perspectiveRotate: 0,
		perspectiveScale: 1,
		perspectiveAspect: 0,
		perspectiveOffsetX: 0,
		perspectiveOffsetY: 0,

		grainAmount: 0,
		grainSize: 0.25,
		grainRoughness: 0.5,

		pointColors: [],

		localAdjustments: [],

		healingMasks: [],

		defringePurpleAmount: 0,
		defringeGreenAmount: 0,
		defringePurpleHueRange: [0.713, 0.948],
		defringeGreenHueRange: [0.28, 0.39],

		userLutId: undefined,
		userLutStrength: 1.0,
		userLutTitle: undefined,

		colorMatchImageId: null,
		colorMatchStrength: 1.0,
		colorMatchSkinToneOptimized: true,

		disableProfileLookTable: false,
	}
}

export const getKeysOfDefaultAdjustments = (): string[] => {
	return Object.keys(getDefaultAdjustments())
}

export const defaultMaskAdjustments: MaskGroupAdjustments = {
	temperature: 0,
	tint: 0,
	saturation: 0,
	toningHue: 0,
	toningSaturation: 0,
	exposure: 0,
	contrast: 0,
	highlights: 0,
	shadows: 0,
	whites: 0,
	blacks: 0,
	clarity: 0,
	texture: 0,
	dehaze: 0,
	sharpen: 0,
	pointColors: [],
	strength: 1,
}

export const defaultWhiteBalance = {
	temperature: 6000,
	tint: 0,
}

export function getAdjustmentRange(key: AdjustmentKey): AdjustmentRange {
	switch (key) {
		case "temperature":
			return [2000, 25000]
		case "tint":
			return [-150, 150]
		case "exposure":
			return [-5, 5]
		case "perspectiveRotate":
			return [-10, 10]
		case "perspectiveScale":
			return [0.5, 1.5]
		case "cropStraighten":
			return [-45, 45]
		case "userLutStrength":
		case "colorMatchStrength":
			return [0, 2]
		case "sharpen":
			return [0.0, 1.5]
		case "gradingShadowsHue":
		case "gradingShadowsSaturation":
		case "gradingMidtonesHue":
		case "gradingMidtonesSaturation":
		case "gradingHighlightsHue":
		case "gradingHighlightsSaturation":
		case "gradingGlobalHue":
		case "gradingGlobalSaturation":
		case "gradingBlending":
		case "luminanceNoiseReduction":
		case "colorNoiseReduction":
		case "vignetteFeather":
		case "parametricDarksStop":
		case "parametricMidsStop":
		case "parametricLightsStop":
		case "grainAmount":
		case "grainSize":
		case "grainRoughness":
			return [0, 1]
		case "curvesAll":
		case "curvesRed":
		case "curvesGreen":
		case "curvesBlue":
			return [0, 255]
		default:
			return [-1, 1]
	}
}

export function getLocalAdjustmentRange(key: MaskAdjustmentKey): AdjustmentRange {
	switch (key) {
		case "tint":
			return [-100, 100]
		case "exposure":
			return [-5, 5]
		case "strength":
			return [0, 2]
		default:
			return [-1, 1]
	}
}

const AdjustmentGroupValues = [
	"profile",
	"lut",
	"basic",
	"curves",
	"basicCurves",
	"parametricCurves",
	"HSL",
	"colorGrading",
	"detail",
	"calibration",
	"cropGroup",
	"transform",
	"effects",
	"lensCorrection",
	"vignetteGroup",
	"grainGroup",
	"masking",
	"healing",
] as const

export type AdjustmentGroup = (typeof AdjustmentGroupValues)[number]

export const isAdjustmentGroup = (value: any) => {
	return AdjustmentGroupValues.includes(value)
}
export const isAdjustmentKey = (value: any) => {
	const defaultAdjustments = getDefaultAdjustments()
	return value in defaultAdjustments
}

export const AdjustmentGroups: Record<AdjustmentGroup, AdjustmentKey[] | AdjustmentGroup[]> = {
	profile: ["colorProfile", "disableProfileLookTable"],
	lut: ["userLutId", "userLutTitle", "userLutStrength"],
	basic: [
		"temperature",
		"tint",
		"exposure",
		"contrast",
		"highlights",
		"shadows",
		"whites",
		"blacks",
		"texture",
		"clarity",
		"dehaze",
		"vibrance",
		"saturation",
	],
	curves: ["basicCurves", "parametricCurves"],
	basicCurves: ["curvesAll", "curvesRed", "curvesGreen", "curvesBlue"],
	parametricCurves: [
		"parametricDarksStop",
		"parametricMidsStop",
		"parametricLightsStop",
		"parametricHighlights",
		"parametricLights",
		"parametricDarks",
		"parametricShadows",
	],
	HSL: [
		"hueRed",
		"saturationRed",
		"luminanceRed",
		"hueOrange",
		"saturationOrange",
		"luminanceOrange",
		"hueYellow",
		"saturationYellow",
		"luminanceYellow",
		"hueGreen",
		"saturationGreen",
		"luminanceGreen",
		"hueAqua",
		"saturationAqua",
		"luminanceAqua",
		"hueBlue",
		"saturationBlue",
		"luminanceBlue",
		"huePurple",
		"saturationPurple",
		"luminancePurple",
		"hueMagenta",
		"saturationMagenta",
		"luminanceMagenta",
	],
	colorGrading: [
		"gradingShadowsHue",
		"gradingShadowsSaturation",
		"gradingShadowsLuminance",
		"gradingMidtonesHue",
		"gradingMidtonesSaturation",
		"gradingMidtonesLuminance",
		"gradingHighlightsHue",
		"gradingHighlightsSaturation",
		"gradingHighlightsLuminance",
		"gradingGlobalHue",
		"gradingGlobalSaturation",
		"gradingGlobalLuminance",
		"gradingBlending",
		"gradingBalance",
	],
	detail: ["luminanceNoiseReduction", "colorNoiseReduction", "sharpen"],
	calibration: [
		"calibrationRedHue",
		"calibrationRedSat",
		"calibrationGreenHue",
		"calibrationGreenSat",
		"calibrationBlueHue",
		"calibrationBlueSat",
	],
	cropGroup: ["cropStraighten", "crop", "cropRatio", "orientation", "flipX", "flipY"],
	transform: [
		"lensDistortion",
		"perspectiveVertical",
		"perspectiveHorizontal",
		"perspectiveRotate",
		"perspectiveScale",
		"perspectiveAspect",
		"perspectiveOffsetX",
		"perspectiveOffsetY",
	],
	effects: ["vignetteGroup", "grainGroup"],
	lensCorrection: [
		"correctLensDistortion",
		"lensDistortionCorrectionFactor",
		"correctLensVignette",
		"lensVignetteCorrectionFactor",
		"correctLensChromaticAberration",
	],
	vignetteGroup: ["vignette", "vignetteFeather"],
	grainGroup: ["grainAmount", "grainSize", "grainRoughness"],
	masking: ["localAdjustments"],
	healing: ["healingMasks"],
}

export const DefaultExcludedAdjustmentKeys: AdjustmentKey[] = [
	"cropStraighten",
	"crop",
	"cropRatio",
	"orientation",
	"flipX",
	"flipY",
	"lensDistortion",
	"perspectiveVertical",
	"perspectiveHorizontal",
	"perspectiveRotate",
	"perspectiveScale",
	"perspectiveAspect",
	"perspectiveOffsetX",
	"perspectiveOffsetY",
	"correctLensDistortion",
	"correctLensVignette",
	"lensDistortionCorrectionFactor",
	"lensVignetteCorrectionFactor",
	"correctLensChromaticAberration",
	"enableLensCorrections",
	"healingMasks",
]

const baseMaskAdjustmentKeys = [
	"exposure",
	"contrast",
	"highlights",
	"shadows",
	"whites",
	"blacks",
	"temperature",
	"tint",
	"saturation",
	"texture",
	"clarity",
	"dehaze",
	"sharpen",
] as const

export const objectMaskFocusKeys = ["strength", ...baseMaskAdjustmentKeys] as const
export const radialMaskFocusKeys = ["strength", "feather", ...baseMaskAdjustmentKeys] as const
export const brushMaskFocusKeys = [
	"strength",
	"radius",
	"feather",
	"flow",
	"density",
	...baseMaskAdjustmentKeys,
] as const

export type MaskAdjustmentFocusKey =
	| (typeof baseMaskAdjustmentKeys)[number]
	| (typeof objectMaskFocusKeys)[number]
	| (typeof radialMaskFocusKeys)[number]
	| (typeof brushMaskFocusKeys)[number]

export const interpolateFullAdjustments = (
	adjustmentsA: Adjustments,
	adjustmentsB: Adjustments,
	x: number,
): Adjustments => {
	return interpolateAdjustments(adjustmentsA, adjustmentsB, x) as Adjustments
}

export const scaleUserLutStrength = (userLutStrength: number | undefined, strength: number) => {
	const [min, max] = getAdjustmentRange("userLutStrength")
	return clamp((userLutStrength ?? 1) * strength, min, max)
}

export const applyPresetStrengthToUserLutAdjustments = (
	adjustments: Partial<Adjustments>,
	presetAdjustments: Partial<Adjustments>,
	strength: number,
) => {
	if (!presetAdjustments.userLutId) {
		return adjustments
	}

	return {
		...adjustments,
		userLutStrength: scaleUserLutStrength(presetAdjustments.userLutStrength, strength),
	}
}

export const interpolateAdjustments = (
	adjustmentsA: Partial<Adjustments>,
	adjustmentsB: Partial<Adjustments>,
	x: number,
) => {
	const newAdjustments = { ...adjustmentsB }
	for (const key in adjustmentsB) {
		if (key === "localAdjustments") {
			newAdjustments[key] = adjustmentsB[key]?.map((maskGroup: MaskGroup, index: number) => {
				const maskGroupA = adjustmentsA[key]?.[index] ?? createMaskGroup()
				return interpolateLocalAdjustments(maskGroupA, maskGroup, x)
			})
		} else if (key.startsWith("curves")) {
			const curveB: Curve = adjustmentsB[key]
			const resultPoints = curveB.map((point: number[]) => {
				const xB = point[0]
				const yB = point[1]

				if (xB != undefined && yB != undefined) {
					const curveA: Curve = adjustmentsA[key]
					const pointA = curveA.find((p) => p[0] === xB)
					const yA = pointA ? pointA[1] : xB

					const y = interpolateAdjustmentValue(x, yA, yB, key as AdjustmentKey)
					return [xB, y]
				}
			})

			newAdjustments[key] = resultPoints
		} else if (key === "pointColors") {
			newAdjustments[key] = interpolatePointColors(
				x,
				adjustmentsA[key] ?? [],
				adjustmentsB[key] ?? [],
			)
		} else if (key === "colorProfile") {
			newAdjustments[key] = adjustmentsB[key]
		} else {
			if (typeof adjustmentsB[key] === "number") {
				newAdjustments[key] = interpolateAdjustmentValue(
					x,
					adjustmentsA[key],
					adjustmentsB[key],
					key as AdjustmentKey,
				)
			} else {
				newAdjustments[key] = adjustmentsB[key]
			}
		}
	}

	return guaranteeParametricCurveStopRange(newAdjustments)
}

const guaranteeParametricCurveStopRange = (adjustments: Partial<Adjustments>) => {
	let { parametricLightsStop, parametricMidsStop, parametricDarksStop } = adjustments

	parametricDarksStop = clamp(
		parametricDarksStop ?? 0.25,
		0.0,
		parametricLightsStop ?? 0.75 - 0.1,
	)
	parametricLightsStop = clamp(parametricLightsStop ?? 0.75, parametricDarksStop + 0.1, 1.0)
	parametricMidsStop = clamp(
		parametricMidsStop ?? 0.5,
		parametricDarksStop + 0.05,
		parametricLightsStop - 0.05,
	)

	return { ...adjustments, parametricDarksStop, parametricLightsStop, parametricMidsStop }
}

export const interpolateLocalAdjustments = (
	adjustmentsA: MaskGroup,
	adjustmentsB: MaskGroup,
	x: number,
) => {
	const newAdjustments = { ...adjustmentsB }
	for (const key in adjustmentsB) {
		if (typeof adjustmentsB[key] === "number" && key !== "strength") {
			newAdjustments[key] = interpolateLocalAdjustmentValue(
				x,
				adjustmentsA[key],
				adjustmentsB[key],
				key as MaskAdjustmentKey,
			)
		} else if (key === "pointColors") {
			newAdjustments[key] = interpolatePointColors(
				x,
				adjustmentsA[key] ?? [],
				adjustmentsB[key] ?? [],
			)
		} else {
			newAdjustments[key] = adjustmentsB[key]
		}
	}
	return newAdjustments
}

const interpolateAdjustmentValue = (x: number, a: number, b: number, key: AdjustmentKey) => {
	const result = lerp(x, a, b)
	if (!isNaN(result)) {
		const range = getAdjustmentRange(key)
		return clamp(result, range[0], range[1])
	}
}

const interpolateLocalAdjustmentValue = (
	x: number,
	a: number,
	b: number,
	key: MaskAdjustmentKey,
) => {
	const result = lerp(x, a, b)
	if (!isNaN(result)) {
		const range = getLocalAdjustmentRange(key)
		return clamp(result, range[0], range[1])
	}
}

const interpolatePointColors = (
	x: number,
	pointColorsA: PointColorHSL[],
	pointColorsB: PointColorHSL[],
) => {
	const result = structuredClone(pointColorsB)
	result.forEach((val, index) => {
		const a = pointColorsA[index]
		val.hueShift =
			interpolateAdjustmentValue(x, a?.hueShift ?? 0, val.hueShift, "pointColors") ?? 0
		val.satShift =
			interpolateAdjustmentValue(x, a?.satShift ?? 0, val.satShift, "pointColors") ?? 0
		val.lumShift =
			interpolateAdjustmentValue(x, a?.lumShift ?? 0, val.lumShift, "pointColors") ?? 0
	})
	return result
}

/**
 * Converts Adjustments (including localAdjustments/masks) to an XMP-compatible object.
 * Handles flat adjustments, tone curves, and masks for serialization.
 */
export const getEmbeddableAdjustments = (
	adjustments: Partial<Adjustments>,
	imageInfo?: ImageInfoRecord,
): Record<string, any> => {
	const output: Record<string, any> = { rendererVersion: "1.0" }

	for (const paramName in adjustments) {
		const value = adjustments[paramName as keyof Adjustments]
		if (value !== undefined && typeof value !== "object" && paramName !== "localAdjustments") {
			output[paramName] = value
		}
	}

	// Tone curves
	const addCurve = (curveName: string, points?: Array<[number, number]>) => {
		if (!points) return
		output[`${curveName}`] = points.map(([x, y]) => `${x},${y}`)
	}

	addCurve("curvesAll", adjustments.curvesAll as Array<[number, number]>)
	addCurve("curvesRed", adjustments.curvesRed as Array<[number, number]>)
	addCurve("curvesGreen", adjustments.curvesGreen as Array<[number, number]>)
	addCurve("curvesBlue", adjustments.curvesBlue as Array<[number, number]>)

	// Masks (localAdjustments)
	const localAdjustments = adjustments.localAdjustments
	if (localAdjustments && localAdjustments.length > 0) {
		const maskGroupBasedCorrections: Record<string, any>[] = []
		const correctionMasks: Record<string, any>[] = []

		for (const group of localAdjustments) {
			// MaskGroup adjustments
			const groupObj: Record<string, any> = {}
			if (group.id) groupObj["CorrectionSyncID"] = group.id
			if (group.name) groupObj["CorrectionName"] = group.name
			if (group.active !== undefined) groupObj["CorrectionActive"] = group.active
			if (group.strength !== undefined) groupObj["CorrectionAmount"] = group.strength

			const excludedMaskGroupFields = new Set(["retouching"])

			// Add local adjustment values
			for (const paramName in group) {
				if (
					paramName !== "masks" &&
					!excludedMaskGroupFields.has(paramName) &&
					group[paramName as keyof MaskGroup] !== undefined &&
					typeof group[paramName as keyof MaskGroup] !== "object"
				) {
					groupObj[paramName] = group[paramName as keyof MaskGroup]
				}
			}
			maskGroupBasedCorrections.push(groupObj)

			// Masks for this group
			const masksArr: Record<string, any>[] = []
			for (const mask of group.masks ?? []) {
				const maskObj: Record<string, any> = {}
				if (mask.id) maskObj["MaskSyncID"] = mask.id
				if (mask.name) maskObj["MaskName"] = mask.name
				if (mask.blendMode) maskObj["MaskBlendMode"] = mask.blendMode === "add" ? "0" : "1"
				if (mask.inverted !== undefined) maskObj["MaskInverted"] = mask.inverted
				if (mask.active !== undefined) maskObj["MaskActive"] = mask.active

				// Mask type
				switch (mask.type) {
					case "radial":
						maskObj["What"] = "Mask/CircularGradient"
						if (mask.centerX !== undefined)
							maskObj["Left"] = mask.centerX - mask.radiusX
						if (mask.centerY !== undefined) maskObj["Top"] = mask.centerY - mask.radiusY
						if (mask.centerX !== undefined)
							maskObj["Right"] = mask.centerX + mask.radiusX
						if (mask.centerY !== undefined)
							maskObj["Bottom"] = mask.centerY + mask.radiusY
						if (mask.angle !== undefined) maskObj["Angle"] = -mask.angle
						if (mask.feather !== undefined)
							maskObj["Feather"] = (mask.feather ?? 0) * 100
						break
					case "gradient":
						maskObj["What"] = "Mask/Gradient"
						if (mask.startX !== undefined) maskObj["FullX"] = mask.startX
						if (mask.startY !== undefined) maskObj["FullY"] = mask.startY
						if (mask.endX !== undefined) maskObj["ZeroX"] = mask.endX
						if (mask.endY !== undefined) maskObj["ZeroY"] = mask.endY
						break
					case "background":
						maskObj["What"] = "Mask/Image"
						maskObj["MaskSubType"] = "0"
						maskObj["MaskSubCategoryID"] = "22"
						break
					case "subject":
						maskObj["What"] = "Mask/Image"
						maskObj["MaskSubType"] = "0"
						maskObj["MaskSubCategoryID"] = "23"
						break
					case "people":
						maskObj["What"] = "Mask/Image"
						maskObj["MaskSubType"] = "0"
						maskObj["MaskSubCategoryID"] = "20036"
						break
					case "sky":
						maskObj["What"] = "Mask/Image"
						maskObj["MaskSubType"] = "2"
						break
				}
				masksArr.push(maskObj)
			}
			correctionMasks.push(masksArr)
		}

		output["MaskGroupBasedCorrections"] = maskGroupBasedCorrections
		output["CorrectionMasks"] = correctionMasks
	}

	// Additional fields if supplied
	output["initialTemperature"] = imageInfo?.features.meta.init_temp
	output["initialTint"] = imageInfo?.features.meta.init_tint

	return output
}

export function extractEmbeddedAdjustmentsFromXMP(xmp: string): Partial<Adjustments> | undefined {
	const parser = new DOMParser()
	const xml = parser.parseFromString(xmp, "application/xml")

	const rdfNS = "http://www.w3.org/1999/02/22-rdf-syntax-ns#"
	const pnNS = "https://next.polarr.com/app/ns/schema"

	const descList = xml.getElementsByTagNameNS(rdfNS, "Description")
	const desc = descList[0]
	if (!desc) return undefined

	const isPolarrXMP = Array.from(desc.attributes).some((attr) => attr.name.startsWith("pn:"))
	if (!isPolarrXMP) {
		return undefined
	}

	const adjustments: Partial<Adjustments> = {}

	// Extract flat pn: attributes
	for (const attr of Array.from(desc.attributes)) {
		if (attr.name.startsWith("pn:")) {
			const key = attr.name.slice(3)
			let value: any = attr.value

			if (value === "true") value = true
			else if (value === "false") value = false
			else if (!isNaN(Number(value))) value = Number(value)

			adjustments[key] = value
		}
	}

	// Helper to parse curve arrays using getElementsByTagNameNS
	const parseCurve = (curveName: string) => {
		const curveElem = desc.getElementsByTagNameNS(pnNS, curveName)[0]
		if (!curveElem) return undefined
		const bag = curveElem.getElementsByTagNameNS(rdfNS, "Bag")[0]
		if (!bag) return undefined
		const points: [number, number][] = []
		for (const li of Array.from(bag.getElementsByTagNameNS(rdfNS, "li"))) {
			const text = li.textContent ?? ""
			const [x, y] = text.split(",").map(Number)
			if (x != null && y != null) {
				points.push([x, y])
			}
		}
		return points
	}

	adjustments.curvesAll = parseCurve("curvesAll")
	adjustments.curvesRed = parseCurve("curvesRed")
	adjustments.curvesGreen = parseCurve("curvesGreen")
	adjustments.curvesBlue = parseCurve("curvesBlue")

	return adjustments
}
